// @flow

import {useEffect, useState} from 'react';

import {EventEmitter} from './EventEmmiter';

import {ListResponseType, ProcedureVersionSerializedType} from 'sweet-types';

import type {ProcedureContentType} from 'app/tasks/types';

export const getEditableContent = (
  procedure: ProcedureVersionSerializedType
): ProcedureContentType => ({
  url: procedure.url,
  html_url: procedure.html_url,
  name: procedure.name,
  is_large: false,
  description: procedure.description,
  content: procedure.content,
  lanes: {},
});

export function nowISOString(): string {
  return new Date().toISOString();
}

export const waitAndReturn = <T>(
  value: T,
  interval = 50
): Promise<T extends () => any ? ReturnType<T> : T> =>
  new Promise((resolve) =>
    setTimeout(
      () => resolve(typeof value === 'function' ? value() : value),
      // speed up demo tests
      process.env.NODE_ENV === 'test' ? 1 : interval
    )
  );

export class EventPublisher {
  topics: {[topic: string]: Array<(data: unknown) => unknown>} = {};
  hasListeners = this.topics.hasOwnProperty;

  subscribe<D>(
    topic: string,
    listener: (data: D) => unknown
  ): {remove: () => void} {
    // Create the topic's object if not yet created
    if (!this.hasListeners.call(this.topics, topic)) this.topics[topic] = [];

    // Add the listener to queue
    const index = this.topics[topic].push(listener) - 1;

    // Provide handle back for removal of topic
    return {remove: () => delete this.topics[topic][index]};
  }

  publish<D>(topic: string, data: D): void {
    // If the topic doesn't exist, or there's no listeners in queue, just leave
    if (!this.hasListeners.call(this.topics, topic)) {
      return;
    }

    // Cycle through topics queue, fire!
    this.topics[topic].forEach((listener) => listener(data));
  }
}

export const createResponseList = <T>(
  elements: Array<T>
): ListResponseType<T> => {
  return {
    count: elements.length,
    next: null,
    previous: null,
    results: elements,
    num_pages: 1,
  };
};
export const useData = <T>(
  initialSource: T,
  updateEvent: string,
  eventEmitter: EventEmitter
): {data: T; isFetching: boolean} => {
  const [data, setData] = useState(initialSource);
  useEffect(() => {
    const onUpdate = (updated) => {
      setData({...updated});
    };
    eventEmitter.on(updateEvent, (updated) => setData({...updated}));
    return () => eventEmitter.off(updateEvent, onUpdate);
  }, []);

  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    setIsFetching(true);
  }, [data]);

  useEffect(() => {
    if (isFetching) {
      setIsFetching(false);
    }
  }, [isFetching]);

  return {
    isFetching,
    data,
  };
};
